import { useMemo, useState, type FormEvent } from "react";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { useUserProfile } from "../../auth/useUserProfile.ts";
import { OnboardingShell } from "../../components/onboarding/OnboardingShell.tsx";
import * as f from "../../components/onboarding/onboarding-form.styles.ts";
import { TOTAL_ONBOARDING_STEPS, getStepNumber } from "../../onboarding/steps.ts";
import { getBrowserClient } from "../../supabase/browser.ts";
import * as jr from "./jurisdiction-gate.styles.ts";

type Selection = "GH" | "NG" | "OTHER";

export function JurisdictionGatePage(): JSX.Element {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { signOut } = useAuthSession();
  const { profile, refetch } = useUserProfile();

  const profileCountry = profile?.country_code ?? null;
  const profileSupported = profile?.jurisdiction_supported === true;
  const isUnsupportedSaved = profileCountry !== null && !profileSupported;

  // Force-show the form so an unsupported user can change country.
  const [forceForm, setForceForm] = useState(false);
  const showBlocked = isUnsupportedSaved && !forceForm;

  const [selection, setSelection] = useState<Selection>("GH");
  const [otherCode, setOtherCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function handleOtherChange(value: string): void {
    setOtherCode(value.toUpperCase().slice(0, 2));
  }

  function resolveCountry(): string | null {
    if (selection === "GH") return "GH";
    if (selection === "NG") return "NG";
    return otherCode.length === 2 ? otherCode : null;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const country = resolveCountry();
    if (!country) {
      setError("Enter a 2-letter ISO country code (e.g. KE).");
      return;
    }
    setError(null);
    setBusy(true);
    const { error: rpcError } = await supabase.rpc("confirm_jurisdiction", {
      p_country_code: country,
    });
    if (rpcError) {
      setError(rpcError.message);
      setBusy(false);
      return;
    }
    setForceForm(false);
    await refetch();
    // AuthGate evaluates jurisdiction_supported. On true → /dashboard. On
    // false → stays on this page; component re-renders showing blocked view.
  }

  if (showBlocked) {
    return (
      <OnboardingShell
        stepNumber={getStepNumber("jurisdiction")}
        totalSteps={TOTAL_ONBOARDING_STEPS}
        eyebrow="Region not supported"
        title={`STRIKZA isn't available in ${profileCountry} yet`}
        description={
          "STRIKZA Phase 1A only enables real-stake recommendations for users in Ghana and " +
          "Nigeria. Phantom and informational modes may open up in this region in the future. " +
          "If you selected the wrong country, change it below — otherwise sign out and we'll be " +
          "in touch when STRIKZA expands."
        }
      >
        <div style={f.buttonRow}>
          <button
            type="button"
            style={f.primaryBtn}
            onClick={() => {
              setForceForm(true);
              setSelection("GH");
              setOtherCode("");
              setError(null);
            }}
          >
            Change country
          </button>
          <button type="button" style={f.secondaryBtn} onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </OnboardingShell>
    );
  }

  return (
    <OnboardingShell
      stepNumber={getStepNumber("jurisdiction")}
      totalSteps={TOTAL_ONBOARDING_STEPS}
      title="Choose your supported jurisdiction"
      description={
        "STRIKZA Phase 1A supports Ghana and Nigeria for real-stake eligibility checks. " +
        "Other countries can continue in informational mode later. This step does not enable " +
        "real-stake recommendations."
      }
    >
      <form style={f.formStack} onSubmit={handleSubmit} noValidate>
        {error ? <div style={f.message("error")}>{error}</div> : null}

        <div style={jr.radioGroup} role="radiogroup" aria-label="Country">
          {(
            [
              { value: "GH" as Selection, title: "Ghana", code: "GH" },
              { value: "NG" as Selection, title: "Nigeria", code: "NG" },
              { value: "OTHER" as Selection, title: "Other / unsupported", code: "Enter ISO code" },
            ] as const
          ).map(({ value, title, code }) => {
            const isSel = selection === value;
            return (
              <label key={value} style={isSel ? jr.radioRowSelected : jr.radioRow}>
                <input
                  type="radio"
                  name="jurisdiction"
                  value={value}
                  checked={isSel}
                  onChange={() => setSelection(value)}
                  style={jr.radioInput}
                />
                <span style={jr.radioLabel}>
                  <span style={jr.radioTitle}>{title}</span>
                  <span style={jr.radioMeta}>{code}</span>
                </span>
              </label>
            );
          })}
        </div>

        {selection === "OTHER" ? (
          <div style={jr.otherInputBlock}>
            <label htmlFor="other-country" style={f.label}>
              Country (ISO 2-letter)
            </label>
            <input
              id="other-country"
              type="text"
              required
              maxLength={2}
              value={otherCode}
              onChange={(e) => handleOtherChange(e.target.value)}
              style={f.input}
            />
            <span style={f.labelHint}>
              We&apos;ll record your country and let you know if real-stake recommendations open up
              there.
            </span>
          </div>
        ) : null}

        <div style={f.buttonRow}>
          <button type="submit" style={f.primaryBtn} disabled={busy}>
            {busy ? "..." : "Continue"}
          </button>
        </div>
      </form>
    </OnboardingShell>
  );
}
